/*
 * Created on 02.05.2016
 *
 * Copyright(c) 1995 - 2016 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */
import { Component, ContentChild, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, TemplateRef, ViewChildren } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest } from 'rxjs';
import { filter, map, skip, startWith, takeUntil } from 'rxjs/operators';
import { dropdownAnimation } from '../../../common-ui-common-animations';
import { SPP_OVERLAY_POSITION_DROPDOWN } from '../../../common-ui-common.config';
import { KeyboardItemDirective } from '../../../keyboard/keyboard-item.directive';
import { KeyboardUtils } from '../../../keyboard/keyboard.utils';
import { SelectItem } from './form-select-item';

/**
 * @deprecated Use {@link SelectItem}[]
 */
export type FormSelectItems = any[] | { [item: string]: string };

/**
 * Diese Komponente ist eine interne Komponente, um eine SelectBox darzustellen.
 * Die Komponente ist eine Erweiterung von {@link FormInputComponent}.
 * <b>Sie soll nicht direkt benutzt werden</b> sondern mittels
 *
 * <pre>
 * <common-form-input type="select"></common-form-input>
 * </pre>
 *
 * @author imam
 */
@Component({
    selector: 'common-form-select',
    templateUrl: 'form-select.component.html',
    animations: [
        dropdownAnimation()
    ]
})
export class FormSelectComponent implements OnInit, OnChanges, OnDestroy {
    /**
     * Die Items, die ausgewält werden. Siehe {@link SelectItem}.
     */
    @Input() items: SelectItem[];
    /**
     * Die CSS-Klasse, die an den items-container angehangen wird.
     * z.B. "large".
     */
    @Input('class') cssClass: string;
    /**
     * Siehe {@link FormInputComponent#displayValue}.
     */
    @Input() displayValue: string;
    /**
     * Siehe {@link FormInputComponent#modelValue}.
     */
    @Input() modelValue: string;
    /**
     * Siehe {@link FormInputComponent#resetText}.
     */
    @Input() resetText: string;
    /**
     * Siehe {@link FormInputComponent#showReset}.
     */
    @Input() showReset: boolean;
    /**
     * Siehe {@link FormInputComponent#showScrollbar}.
     */
    @Input() showScrollbar: boolean;
    /**
     * Siehe {@link FormInputComponent#disabled}.
     */
    @Input() disabled: boolean;
    /**
     * Gibt ein Event aus mit dem Model-Value des ausgewählten Elements.
     */
    @Output() selected: Subject<any> = new Subject();
    /**
     * Gibt ein Event aus, wenn das Feld resettet wird.
     */
    @Output() reset: EventEmitter<any> = new EventEmitter();

    @ContentChild('selectInput') selectInput;

    @ViewChildren(KeyboardItemDirective) liItems: QueryList<TemplateRef<HTMLLIElement>>;

    isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    options$ = new BehaviorSubject<{ label: string, value: string; }[]>([]);

    overlayPosition = SPP_OVERLAY_POSITION_DROPDOWN;

    /**
     * Options-Index. Wird in der CommonKeyboardList verwendet
     */
    selectedItemId$: Observable<number>;

    private ngUnsubscribe$: Subject<void> = new Subject<void>();

    private model$ = new ReplaySubject();

    @Input('model')
    set model(value: any) {
        this.model$.next(value);
    }

    constructor() {
        // option keys sind string
        const modelString$ = this.model$.pipe(
            map(model => '' + model)
        );
        /**
         * Wenn das Modell oder Items sich ändern, wird dementsprechend ein neuer Item ID gesucht.
         */
        this.selectedItemId$ = combineLatest([modelString$, this.options$]).pipe(
            map(([model, options]) => options.findIndex(item => item.value === model)),
            filter(index => index !== -1),
            startWith(0),
            takeUntil(this.ngUnsubscribe$)
        );
    }

    ngOnInit(): void {
        this.isOpen$.pipe(
            skip(1),
            filter(isOpen => !isOpen),
            takeUntil(this.ngUnsubscribe$)
        )
            .subscribe(() => this.focusOnInput());
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event) { // KeyboardEvent
        if (KeyboardUtils.keyboardEventSearchable(event) && !this.isOpen$.getValue()) {
            this.onKeySearch(event.key);
        }
        if (KeyboardUtils.isKeyboardAction(event, 'down')) {
            event.preventDefault();
        }
    }

    @HostListener('keyup', ['$event'])
    onKeyUp(event) { // KeyboardEvent
        if (KeyboardUtils.isKeyboardAction(event, 'down')) {
            this.open();
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.isOpen$.complete();
        this.model$.complete();
        this.options$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('items') && changes['items'].currentValue) {
            const items: SelectItem[] = changes['items'].currentValue;
            let options;

            if (this.displayValue && items instanceof Array) {
                // Fall 1: items ist Array von Objekten und displayValue wurde angegeben
                // -> Jeweils die Property "displayValue" der einzelnen Objekte wird angezeigt.
                options = items.map(item => ({
                    label: item[this.displayValue],
                    value: '' + item[this.modelValue]
                }));
            } else {
                // Fall 2: items ist ein Objekt (Dictionary) -> Die Werte der Properties des Objekts werden angezeigt.
                // !!!Fall 3: items ist ein Array -> Die einzelnen Elemente des Arrays werden angezeigt. (Nur sinnvoll bei String-Arrays)
                // x wird immer string https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Property_Accessors
                options = Object.keys(items).map(x => ({
                    label: items[x],
                    value: x
                }));
            }
            this.options$.next(options);
        }
    }

    onSelected(event: Event, index: number) {
        event.preventDefault();
        this.selectIndex(index);
        this.close();
    }

    onReset() {
        this.close();
        this.reset.emit();
    }

    toggleDropdown(): void {
        if (this.disabled) return;
        this.isOpen$.next(!this.isOpen$.getValue());
    }

    close() {
        this.isOpen$.next(false);
    }

    open() {
        this.isOpen$.next(true);
    }

    private selectIndex(index: number) {
        if (index === undefined) {
            return;
        }
        if (this.displayValue && this.items instanceof Array) {
            // TODO Analyse im Rahmen dieses Commits hat ergeben, dass das Legacy-Code ist
            //  der gar nicht oder nur sehr eingeschränkt funktioniert; nutzen tatsächlich nur
            //  noch Array von Objecten mit displayValue und modelValue
            // Fall 1: items ist Array von Objekten und displayValue wurde angegeben
            // -> Das Model ist das Element des Arrays an Position index.
            return this.selected.next(this.items[index]);
        } else {
            // Fall 2: items ist ein Objekt (Dictionary) -> Das Model ist der Key des Dicitionaries an Position index.
            // TODO Analyse im Rahmen dieses Commits hat ergeben, dass das ("Fall 3") Legacy-Code ist
            //  der gar nicht oder nur sehr eingeschränkt funktioniert; nutzen tatsächlich nur
            //  noch Array von Objecten mit displayValue und modelValue
            // !!!Fall 3: items ist ein Array -> Das Model ist index (denn Object.keys() liefert für Arrays die Index-Werte).
            return this.selected.next(Object.keys(this.items)[index]);
        }
    }

    private focusOnInput() {
        if (this.selectInput) {
            this.selectInput.nativeElement.focus();
        }
    }

    private onKeySearch(searchChart: string) {
        if (!searchChart) {
            return;
        }
        // todo: use commonKeyboardSymbolSearch
        const searchIndex = this.options$.getValue().findIndex(item => item.label[0].toLowerCase() === searchChart.toLowerCase());
        if (searchIndex !== -1) {
            this.selectIndex(searchIndex);
        }
    }
}
